import React from 'react';

import { ArrowUpRightIcon, EditIcon } from '@infinex/ui-icons';
import { Text } from '@infinex/ui/components';

import type { ProgramDetails } from '../helpers/solanaHelpers';

interface SolanaProgramsProps {
  programs: ProgramDetails[];
  onExplorerClick: (address: string) => void;
}

interface AccountGroupProps {
  title: string;
  accounts: ProgramDetails[];
  count: number;
  isWriteAccess?: boolean;
  defaultOpen?: boolean;
  borderColor: string;
  onExplorerClick: (address: string) => void;
}

function AccountGroup({
  title,
  accounts,
  count,
  isWriteAccess = false,
  defaultOpen = false,
  borderColor,
  onExplorerClick,
}: AccountGroupProps) {
  if (count === 0) return null;

  return (
    <details open={defaultOpen} className={`${borderColor} border-l-2 pl-3`}>
      <summary className="text-default hover:text-emphasis flex cursor-pointer items-center gap-2 text-sm font-medium transition-colors">
        <span className="flex-1">
          {title} <span className="text-tertiary font-normal">({count})</span>
        </span>
        {isWriteAccess && <EditIcon fill="caution" className="size-3.5" />}
      </summary>
      <div className="mt-2 ml-4 space-y-1.5">
        {accounts.map((account) => (
          <div
            key={account.address}
            className="group flex cursor-pointer items-center gap-2 py-0.5"
            onClick={() => onExplorerClick(account.address)}
          >
            <Text className="text-default group-hover:text-brand min-w-0 flex-1 truncate text-sm transition-colors">
              {account.name}
            </Text>
            <ArrowUpRightIcon className="fill-tertiary group-hover:fill-brand size-3.5 shrink-0 transition-colors" />
          </div>
        ))}
      </div>
    </details>
  );
}

export function SolanaPrograms({
  programs,
  onExplorerClick,
}: SolanaProgramsProps) {
  // Categorize accounts by type and write status
  const writtenPDAs = programs.filter(
    (p) => p.type === 'PDA' && p.was_written_to === true
  );
  const writtenTokenAccounts = programs.filter(
    (p) => p.type === 'TOKEN_ACCOUNT' && p.was_written_to === true
  );
  const writtenPrograms = programs.filter(
    (p) => p.type === 'PROGRAM' && p.was_written_to === true
  );

  const readPDAs = programs.filter(
    (p) => p.type === 'PDA' && p.was_written_to !== true
  );
  const readTokenAccounts = programs.filter(
    (p) => p.type === 'TOKEN_ACCOUNT' && p.was_written_to !== true
  );
  const readPrograms = programs.filter(
    (p) => p.type === 'PROGRAM' && p.was_written_to !== true
  );

  const nativePrograms = programs.filter((p) => p.type === 'NATIVE_PROGRAM');

  return (
    <div className="space-y-2">
      <div className="mb-3 flex items-center justify-between">
        <Text className="text-default text-sm font-semibold">
          Programs & Accounts
        </Text>
      </div>

      {/* Written accounts - Most sensitive */}
      <AccountGroup
        title="PDAs"
        accounts={writtenPDAs}
        count={writtenPDAs.length}
        isWriteAccess
        defaultOpen
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      <AccountGroup
        title="Token Accounts"
        accounts={writtenTokenAccounts}
        count={writtenTokenAccounts.length}
        isWriteAccess
        defaultOpen
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      <AccountGroup
        title="Programs"
        accounts={writtenPrograms}
        count={writtenPrograms.length}
        isWriteAccess
        defaultOpen
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      {/* Read-only accounts */}
      <AccountGroup
        title="PDAs"
        accounts={readPDAs}
        count={readPDAs.length}
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      <AccountGroup
        title="Token Accounts"
        accounts={readTokenAccounts}
        count={readTokenAccounts.length}
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      <AccountGroup
        title="Programs"
        accounts={readPrograms}
        count={readPrograms.length}
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />

      {/* System Programs - Least important */}
      <AccountGroup
        title="System Programs"
        accounts={nativePrograms}
        count={nativePrograms.length}
        borderColor="border-warning/30"
        onExplorerClick={onExplorerClick}
      />
    </div>
  );
}
